import { AssetDTO, FormDTO, FormElement, FormElementKind, validateUnitReadingAnswer } from '@gearbelt/global';
import { Controller, useFormContext } from 'react-hook-form';
import { match } from 'ts-pattern';

import { AssetSelectFormElement } from './elements/asset-select';
import { DateFormElement } from './elements/date';
import { DropdownFormElement } from './elements/dropdown';
import { IssuesSelectFormElement } from './elements/issue-select';
import { LocationSelectFormElement } from './elements/location-select';
import { MultiLineInputFormElement } from './elements/multi-line-input';
import { NumberFormElement } from './elements/number';
import { PassFailCheckFormElement } from './elements/pass-fail-check';
import { PdfAttachmentFormElement } from './elements/pdf-attachment';
import { PhotoFormElement } from './elements/photo';
import { PreFilledTextFormElement } from './elements/pre-filled-text';
import { SignatureArrayFormElement } from './elements/signature-array';
import { SingleLineInputFormElement } from './elements/single-line-input';
import { SpaceFormElement } from './elements/space';
import { TableFormElement } from './elements/table';
import { UnitReadingFormElement } from './elements/unit-reading';

// Element kinds produced by older clients that are no longer in FormElementKind. They still appear in
// historical form data, so they're handled before the exhaustive match below — which stays exhaustive
// over the current FormElementKind union so new kinds are still caught at compile time.
const LEGACY_FORM_ELEMENT_KINDS = ['schedule-type-select', 'check'];

function isLegacyFormElementKind(kind: string): boolean {
  return LEGACY_FORM_ELEMENT_KINDS.includes(kind);
}

function LegacyFormElementFallback({ label }: { label: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium">{label}</span>
      <span className="text-secondary-text text-sm">This field type is no longer supported.</span>
    </div>
  );
}

export function FormElementComponent({
  element,
  asset,
  form,
  readonly,
  lockedNotLatest = false,
  isEditingExistingRecord = false,
  isPreview,
  formLocationId,
  onBeforeChange,
}: {
  element: FormElement;
  readonly: boolean;
  lockedNotLatest?: boolean;
  // True when editing a record that already exists. The unit-reading lower guard is create-time only, matching the API's skipUnitReadingLowerCheck.
  isEditingExistingRecord?: boolean;
  asset: AssetDTO;
  form: FormDTO;
  isPreview: boolean;
  formLocationId?: string;
  // Lets a trigger's new answer be held back before it reaches the form.
  onBeforeChange?: (nextValue: unknown, applyChange: () => void) => void;
}) {
  const { control, setValue } = useFormContext();

  if (isLegacyFormElementKind(element.kind)) {
    return <LegacyFormElementFallback label={element.label} />;
  }

  return match(element)
    .with({ kind: FormElementKind.SingleLineInput }, (textElement) => (
      <Controller
        control={control}
        name={element.id}
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
          <SingleLineInputFormElement element={textElement} value={value} onChange={onChange} onBlur={onBlur} readonly={readonly} error={error?.message} />
        )}
        rules={{ required: { value: element.required, message: 'This field is required' } }}
      />
    ))
    .with({ kind: FormElementKind.PassFailCheck }, (PassFailCheckElement) => (
      <Controller
        control={control}
        name={element.id}
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
          <PassFailCheckFormElement
            element={PassFailCheckElement}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            asset={asset}
            readonly={readonly}
            form={form}
            error={error?.message}
            locationId={formLocationId ?? asset.locationId ?? undefined}
          />
        )}
        rules={{
          required: { value: element.required, message: 'This field is required' },
          validate: (value) => {
            if (value?.option === null && (!value?.issues || value.issues.length === 0)) {
              return 'Please create an issue';
            }
            return true;
          },
        }}
      />
    ))
    .with({ kind: FormElementKind.PassFailWithNaCheck }, (PassFailCheckWithNaElement) => (
      <Controller
        control={control}
        name={element.id}
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
          <PassFailCheckFormElement
            element={PassFailCheckWithNaElement}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            asset={asset}
            readonly={readonly}
            form={form}
            error={error?.message}
            locationId={formLocationId ?? asset.locationId ?? undefined}
          />
        )}
        rules={{
          required: { value: element.required, message: 'This field is required' },
          validate: (value) => {
            if (value?.option === null && (!value?.issues || value.issues.length === 0)) {
              return 'Please create an issue';
            }
            return true;
          },
        }}
      />
    ))
    .with({ kind: FormElementKind.AssetSelect }, (assetSelectElement) => (
      <Controller
        control={control}
        name={element.id}
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
          <AssetSelectFormElement
            element={assetSelectElement}
            value={value}
            {...(readonly
              ? {
                  defaultAssetId: value,
                }
              : {})}
            onChange={onChange}
            onBlur={onBlur}
            readonly={readonly}
            error={error?.message}
          />
        )}
        rules={{ required: { value: element.required, message: 'This field is required' } }}
      />
    ))
    .with({ kind: FormElementKind.SignatureArray }, (signatureArrayElement) => (
      <Controller
        control={control}
        name={element.id}
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
          <SignatureArrayFormElement
            element={signatureArrayElement}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            readonly={readonly}
            isPreview={isPreview}
            error={error?.message}
          />
        )}
        rules={{ required: { value: element.required, message: 'This field is required' } }}
      />
    ))
    .with({ kind: FormElementKind.PreFilledText }, (contentElement) => <PreFilledTextFormElement element={contentElement} />)
    .with({ kind: FormElementKind.Date }, (dateElement) => (
      <Controller
        control={control}
        name={element.id}
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
          <DateFormElement
            element={dateElement}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            readonly={readonly}
            lockedNotLatest={lockedNotLatest}
            error={error?.message}
          />
        )}
        rules={{ required: { value: element.required, message: 'This field is required' } }}
      />
    ))
    .with({ kind: FormElementKind.IssueSelect }, (issueSelectElement) => (
      <Controller
        control={control}
        name={element.id}
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
          <IssuesSelectFormElement
            element={issueSelectElement}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            readonly={readonly}
            asset={asset}
            form={form}
            error={error?.message}
            locationId={formLocationId ?? asset.locationId ?? undefined}
          />
        )}
        rules={{
          required: { value: element.required, message: 'This field is required' },
          validate: element.required ? (value) => (Array.isArray(value) && value.length > 0) || 'A valid issue must be selected' : undefined,
        }}
      />
    ))
    .with({ kind: FormElementKind.Heading }, () => null) // Deprecated
    .with({ kind: FormElementKind.Dropdown }, (dropdownElement) => (
      <Controller
        control={control}
        name={element.id}
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
          <DropdownFormElement
            element={dropdownElement}
            value={value}
            onChange={onBeforeChange ? (nextValue) => onBeforeChange(nextValue, () => onChange(nextValue)) : onChange}
            onBlur={onBlur}
            readonly={readonly}
            error={error?.message}
          />
        )}
        rules={{ required: { value: element.required, message: 'This field is required' } }}
      />
    ))
    .with({ kind: FormElementKind.LocationSelect }, (locationSelectElement) => (
      <Controller
        control={control}
        name={element.id}
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
          <LocationSelectFormElement
            element={locationSelectElement}
            assetLocationId={asset.locationId || undefined}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            onPrefill={(next) => setValue(element.id, next, { shouldDirty: false })}
            readonly={readonly}
            error={error?.message}
          />
        )}
        rules={{ required: { value: element.required, message: 'This field is required' } }}
      />
    ))
    .with({ kind: FormElementKind.Number }, (numberElement) => (
      <Controller
        control={control}
        name={element.id}
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
          <NumberFormElement element={numberElement} value={value} onChange={onChange} onBlur={onBlur} readonly={readonly} error={error?.message} />
        )}
        rules={{ required: { value: element.required, message: 'This field is required' } }}
      />
    ))
    .with({ kind: FormElementKind.Photo }, (photoElement) => (
      <Controller
        control={control}
        name={element.id}
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
          <PhotoFormElement
            element={photoElement}
            value={value === '' ? [] : value}
            onChange={onChange}
            onBlur={onBlur}
            readonly={readonly}
            isPreview={isPreview}
            error={error?.message}
          />
        )}
        rules={{ required: { value: element.required, message: 'This field is required' } }}
      />
    ))
    .with({ kind: FormElementKind.MultiLineInput }, (multiLineInputElement) => (
      <Controller
        control={control}
        name={element.id}
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
          <MultiLineInputFormElement
            element={multiLineInputElement}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            readonly={readonly}
            error={error?.message}
          />
        )}
        rules={{
          required: { value: element.required, message: 'This field is required' },
        }}
      />
    ))
    .with({ kind: FormElementKind.Table }, (tableElement) => <TableFormElement element={tableElement} readonly={readonly} />)
    .with({ kind: FormElementKind.Space }, () => <SpaceFormElement />)
    .with({ kind: FormElementKind.UnitReading }, (unitReadingElement) => (
      <Controller
        control={control}
        name={element.id}
        render={({ field: { value, onChange, onBlur }, fieldState: { error, isTouched } }) => (
          <UnitReadingFormElement
            element={unitReadingElement}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            asset={asset}
            readonly={readonly}
            lockedNotLatest={lockedNotLatest}
            isEditingExistingRecord={isEditingExistingRecord}
            error={error?.message}
            isTouched={isTouched}
          />
        )}
        rules={{
          required: { value: element.required, message: 'This field is required' },
          validate: (value) =>
            validateUnitReadingAnswer({
              value,
              config: unitReadingElement.config,
              unitReadings: asset.unitReadings,
              isEditingExistingRecord,
            }),
        }}
      />
    ))
    .with({ kind: FormElementKind.PdfAttachment }, (pdfAttachmentElement) => (
      <Controller
        control={control}
        name={element.id}
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
          <PdfAttachmentFormElement
            element={pdfAttachmentElement}
            value={value === '' ? [] : value}
            onChange={onChange}
            onBlur={onBlur}
            readonly={readonly}
            isPreview={isPreview}
            error={error?.message}
          />
        )}
        rules={{ required: { value: element.required, message: 'This field is required' } }}
      />
    ))
    .with({ kind: FormElementKind.ReadingHours }, () => null) // Deprecated
    .with({ kind: FormElementKind.ReadingMileage }, () => null) // Deprecated
    .exhaustive();
}
